import { useDispatch } from 'react-redux'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { getIsRetailOrderLineItem } from '~/components/dashboard/invoices/invoiceUtils'
import { editRetailOrderLineItem } from '~/store/actions/finance'
import { editChargeSheetItem } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'

import { handleBundleDeclineUpdates } from '../utils/tableHelpers'
import type { OptimisticOverride } from './useOptimisticItemUpdates'

type ApplyOptimisticFunc = (itemId: string, changes: OptimisticOverride) => void

/**
 * Hook for handling item and bundle decline/undecline
 * Supports both InvoiceLineItem and RetailOrderLineItem
 * Handles bundle decline for all children
 *
 * @param applyOptimistic - Optional callback to apply optimistic updates
 * @returns Object with declineItem and declineBundle handlers
 */
export const useChargeItemDecline = (applyOptimistic?: ApplyOptimisticFunc) => {
  const dispatch = useDispatch()

  const declineItem = (
    item: InvoiceLineItem | RetailOrderLineItem,
    parentItem?: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    const newDeclinedState = !item.declined
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (isRetailOrder) {
      // Note: We don't apply optimistic updates for retail order items because
      // editRetailOrderLineItem doesn't update editingChargesMap (handled in finance reducer),
      // which means the optimistic state would never be cleared
      dispatch(
        editRetailOrderLineItem({
          id: item.id,
          quantity: item.quantity,
          declined: newDeclinedState,
          partNumber: item.partNumber,
          expectedModificationDate: item.modificationDate,
          logExpectedModificationDate: (item as RetailOrderLineItem)
            .prescription?.updatedAt,
          autoshipFrequency: (item as RetailOrderLineItem).autoshipFrequency,
          autoshipUnitId: (item as RetailOrderLineItem).autoshipUnit?.id,
        }),
      )
    } else {
      // Apply optimistic update immediately if callback provided
      if (applyOptimistic) {
        applyOptimistic(item.id, { declined: newDeclinedState })

        // If undeclining a child of a fully-declined bundle, also undecline the parent
        if (parentItem && !newDeclinedState && parentItem.declined) {
          applyOptimistic(parentItem.id, { declined: false })
        }
      }

      dispatch(
        editChargeSheetItem({
          updateItemInput: [
            {
              id: item.id,
              expectedModification: item.modificationDate,
              expectedSoapLogModification: (item as InvoiceLineItem)
                .soapLogModificationDate,
              declined: newDeclinedState,
            },
          ],
        }),
      )
    }
  }

  const declineBundle = (item: InvoiceLineItem) => {
    const newDeclinedState = !item.declined

    // Apply optimistic updates to bundle parent and all children
    if (applyOptimistic && item.items) {
      applyOptimistic(item.id, { declined: newDeclinedState })
      item.items.forEach((child) => {
        applyOptimistic(child.id, { declined: newDeclinedState })
      })
    }

    if (item.items) {
      handleBundleDeclineUpdates(item, dispatch, newDeclinedState)
    }
  }

  return { declineItem, declineBundle }
}
